import { useCallback } from 'react';

import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from 'redux/store/store.types';
import { capitalize, getApiErrorMessage } from 'utils';

import { EUserRoles, IUser, IUserRole, setCurrentUser } from 'modules/auth-session';

import {
  useBindUserCompaniesMutation,
  useCreateApiKeyMutation,
  useCreateUserMutation,
  useLazyFetchUsersQuery,
  useLazyGetAvailableRolesQuery,
  useLazyGetUserQuery,
  useRemoveApiKeyMutation,
  useRemoveUserMutation,
  useSwitchUserCompanyMutation,
  useUpdatedUserMutation,
  useUpdatedUserProfileMutation,
} from '../api/users.api';
import { UsersAC, setAvailableRoles, setSelectedUser, setUsersModal } from '../store/users.slice';
import {
  BindUserCompaniesPayload,
  CreateApiKeyResponse,
  CreateUserPayload,
  FetchUsersPayload,
  GetUserPayload,
  IUsersModal,
  RemoveUserPayload,
  SwitchUserCompanyPayload,
  UpdateUserPayload,
  UpdateUserProfilePayload,
} from '../types/users.types';

interface OpenModal {
  modal: IUsersModal;
  user?: IUser;
}

interface UseUsers {
  createUserHandler: (payload: CreateUserPayload) => Promise<IUser | null>;
  updateUserHandler: (payload: UpdateUserPayload) => Promise<IUser | null>;
  fetchUsersSimpleHandler: (payload: FetchUsersPayload) => Promise<IUser[]>;
  bindUserCompaniesHandler: (payload: BindUserCompaniesPayload) => Promise<boolean>;
  getUserHandler: (payload: GetUserPayload) => Promise<IUser | null>;
  removeUserHandler: (payload: RemoveUserPayload) => Promise<boolean>;
  fetchUsersHandler: (payload: FetchUsersPayload) => Promise<void>;
  switchUserCompanyHandler: (payload: SwitchUserCompanyPayload) => Promise<boolean>;
  updateUserProfileHandler: (payload: UpdateUserProfilePayload) => Promise<IUser | null>;
  getAvailableRoles: () => Promise<IUserRole[]>;
  openUsersModal: (payload: OpenModal) => void;
  closeUsersModal: () => void;
  clearUsersState: () => void;
  createApiKeyHandler: () => Promise<CreateApiKeyResponse | null>;
  removeApiKeyHandler: () => Promise<boolean>;
}

export const useUsers = (): UseUsers => {
  const dispatch = useAppDispatch();

  const { currentUser } = useAppSelector((state) => state.authSession);

  const [getAvailableRolesQuery] = useLazyGetAvailableRolesQuery();
  const [removeUserQuery] = useRemoveUserMutation();
  const [fetchUsersQuery] = useLazyFetchUsersQuery();
  const [getUserQuery] = useLazyGetUserQuery();
  const [createUserQuery] = useCreateUserMutation();
  const [updateUserQuery] = useUpdatedUserMutation();
  const [bindUserCompaniesQuery] = useBindUserCompaniesMutation();
  const [switchUserCompanyQuery] = useSwitchUserCompanyMutation();
  const [updateUserProfileQuery] = useUpdatedUserProfileMutation();
  const [createApiKeyQuery] = useCreateApiKeyMutation();
  const [removeApiKeyQuery] = useRemoveApiKeyMutation();

  const createApiKeyHandler = async () => {
    dispatch(UsersAC.toggleLoading(true));
    const { data: response, error } = await createApiKeyQuery();
    dispatch(UsersAC.toggleLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to create api key!');
      return null;
    }
    if (currentUser) {
      dispatch(setCurrentUser({ ...currentUser, api_token_created_at: response.data.created_at }));
    }
    toast.success('Api key successfully created!');
    return response.data;
  };

  const removeApiKeyHandler = async () => {
    dispatch(UsersAC.toggleLoading(true));
    const { error } = await removeApiKeyQuery();
    dispatch(UsersAC.toggleLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to remove api key!');
      return false;
    }
    if (currentUser) {
      dispatch(setCurrentUser({ ...currentUser, api_token_created_at: null }));
    }
    toast.success('Api key successfully removed!');
    return true;
  };

  const switchUserCompanyHandler = async (payload: SwitchUserCompanyPayload) => {
    dispatch(UsersAC.toggleLoading(true));
    const { error } = await switchUserCompanyQuery(payload);
    dispatch(UsersAC.toggleLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to switch user company!');
      return false;
    }
    return true;
  };

  const updateUserProfileHandler = async (payload: UpdateUserProfilePayload) => {
    dispatch(UsersAC.toggleLoading(true));
    const { data: response, error } = await updateUserProfileQuery(payload);
    dispatch(UsersAC.toggleLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to update user profile!');
      return null;
    }
    return response.data;
  };

  const updateUserHandler = async (payload: UpdateUserPayload) => {
    dispatch(UsersAC.toggleLoading(true));
    const { data: response, error } = await updateUserQuery(payload);
    dispatch(UsersAC.toggleLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to update user!');
      return null;
    }
    return response.data;
  };

  const createUserHandler = async (payload: CreateUserPayload) => {
    dispatch(UsersAC.toggleLoading(true));
    const { data: response, error } = await createUserQuery(payload);
    dispatch(UsersAC.toggleLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to create user!');
      return null;
    }
    return response.data;
  };

  const bindUserCompaniesHandler = async (payload: BindUserCompaniesPayload) => {
    dispatch(UsersAC.toggleLoading(true));
    const { data: response, error } = await bindUserCompaniesQuery(payload);
    dispatch(UsersAC.toggleLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to bind user companies!');
      return false;
    }
    toast.success('User companies successfully bound!');
    dispatch(UsersAC.editItem(response.data));
    return true;
  };

  const getUserHandler = async (payload: GetUserPayload) => {
    dispatch(UsersAC.toggleLoading(true));
    const { data: response, error } = await getUserQuery(payload);
    dispatch(UsersAC.toggleLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to get user!');
      return null;
    }
    return response.data;
  };

  const fetchUsersSimpleHandler = async (payload: FetchUsersPayload) => {
    const { data: response, error } = await fetchUsersQuery(payload);

    if (error || !response?.data) {
      return [];
    }

    return response.data;
  };

  const fetchUsersHandler = async (payload: FetchUsersPayload) => {
    dispatch(UsersAC.toggleLoading(true));
    const { data: response, error } = await fetchUsersQuery(payload);
    dispatch(UsersAC.toggleLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to fetch users!');
      return;
    }
    batch(() => {
      dispatch(UsersAC.setItems(response.data));
      dispatch(UsersAC.setPaginationMeta(response.meta));
    });
  };

  const getAvailableRoles = useCallback(async () => {
    const { data: response, error } = await getAvailableRolesQuery({ page: 1, per_page: 100 });
    if (error || !response?.data) {
      dispatch(setAvailableRoles([]));
      return [];
    }
    let roles = [...response.data];
    if (currentUser && !currentUser.hasAdminAccess) {
      roles = roles.filter((role) => role.name !== EUserRoles.ADMIN);
    }
    roles = roles
      .filter((role) => Object.values(EUserRoles).includes(role.name))
      .map((role) => ({ ...role, name: capitalize(role.name) as EUserRoles }));
    dispatch(setAvailableRoles(roles));
    return roles;
  }, [currentUser]);

  const removeUserHandler = async (payload: RemoveUserPayload) => {
    dispatch(UsersAC.toggleLoading(true));
    const { error } = await removeUserQuery(payload);
    dispatch(UsersAC.toggleLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to remove user!');
      return false;
    }
    toast.success('User successfully removed!');
    return true;
  };

  const openUsersModal = ({ modal, user }: OpenModal) => {
    batch(() => {
      dispatch(setSelectedUser(user || null));
      dispatch(setUsersModal(modal));
    });
  };

  const closeUsersModal = () => {
    batch(() => {
      dispatch(setSelectedUser(null));
      dispatch(setUsersModal(null));
    });
  };

  const clearUsersState = () => {
    batch(() => {
      dispatch(UsersAC.clearItems());
      dispatch(UsersAC.clearFilters());
      dispatch(UsersAC.clearSorts());
      dispatch(setSelectedUser(null));
      dispatch(setUsersModal(null));
    });
  };

  return {
    updateUserHandler,
    fetchUsersSimpleHandler,
    getAvailableRoles,
    openUsersModal,
    closeUsersModal,
    removeUserHandler,
    fetchUsersHandler,
    clearUsersState,
    getUserHandler,
    bindUserCompaniesHandler,
    createUserHandler,
    switchUserCompanyHandler,
    updateUserProfileHandler,
    createApiKeyHandler,
    removeApiKeyHandler,
  };
};
